import { ComponentIds, } from "@inter-ikea-core/core";
import { MOUNTING_RAIL_RIGHT_SIDE_UPRIGHT_CONNECTORS, MOUNTING_RAIL_TO_UPRIGHT, MOUNTING_RAIL_TO_UPRIGHT_LEFT, UPRIGHT_TO_MOUNTING_RAIL, UPRIGHT_TO_MOUNTING_RAIL_LEFT, } from "../constants/connectorKeys";
import { ADD, DELETE, GUEST, HOST, ROOM_SURFACE, UPDATE, } from "@inter-ikea-storagetwo/range/dist/constants";
import { getGuestConnections } from "@inter-ikea-storagetwo/range/dist/functions/componentGetters";
import { CONNECTORS_EQUAL, NO_CONNECTION_KEY, UNIQUE_CONNECTION_COULD_NOT_BE_FOUND, } from "@inter-ikea-storagetwo/range/dist/constants/errors";
/**
 * Removes all connections that aren't connections regarding uprights and mounting rails
 * @param connections
 */
export const filterOutUprightConnectors = (connections) => connections.filter(({ hostConnector: { key } }) => {
    return isUprightConnector(key);
});
/**
 * Makes sure that a connector has "Upright Connector" abbreviation "UC".
 * @param key
 */
export const isUprightConnector = (key) => key.includes(UPRIGHT_TO_MOUNTING_RAIL) ||
    key.includes(MOUNTING_RAIL_TO_UPRIGHT);
/**
 * Checks if the given key includes constants LEFT and UPRIGHT_CONNECTOR_ABBR, identifying it
 * as a left upright connector.
 *
 * @param {string} key - The key to check for inclusion of LEFT and UPRIGHT_CONNECTOR_ABBR.
 * @returns {boolean} - True if key includes LEFT and UPRIGHT_CONNECTOR_ABBR, false otherwise.
 */
export const isLeftUprightConnector = (key) => key.includes(UPRIGHT_TO_MOUNTING_RAIL_LEFT);
/**
 * Creates a comparable string containing the host and guest entity id and connector key
 *
 * @param hostConnector
 * @param hostConnectorEntity
 * @param guestConnector
 * @param guestConnectorEntity
 */
export const stringifyConnectionData = ({ hostConnector, hostConnectorEntity, guestConnector, guestConnectorEntity, }) => `hostKey:${hostConnector.key}|hostId:${hostConnectorEntity.id}-guestKey:${guestConnector.key}|guestKey:${guestConnectorEntity.id}`;
/**
 * Comparers connections to see if they contain same entity ID's and same connection keys for each connection as
 * previous iteration and returns if so is the case.
 *
 * @param oldConnections
 * @param newConnections
 */
export const identicalConnections = (oldConnections, newConnections) => {
    const [smallest, largest] = [newConnections, oldConnections].sort((a, b) => a.length - b.length);
    const stringifiedSmallest = smallest.map(stringifyConnectionData);
    return largest.every((connection) => stringifiedSmallest.includes(stringifyConnectionData(connection)));
};
export const whatChangedWithConnection = (newConnections, oldConnections) => {
    if (identicalConnections(newConnections, oldConnections))
        throw new Error(CONNECTORS_EQUAL);
    if (oldConnections.length > newConnections.length)
        return DELETE;
    if (newConnections.length > oldConnections.length)
        return ADD;
    return UPDATE;
};
/**
 * Compares Old and new connections of and find the connection that differentiates the two.
 * Used for finding the new or removed connection on lifecycle method onUpdateComponent
 *
 * @param {Connection[]} newConnections - An array of new connections
 * @param {Connection[]} oldConnections - An array of old connections
 *
 * @returns {Connection} The unique connection found between new connections and old connections
 */
export const findUniqueConnections = (newConnections, oldConnections) => {
    if (identicalConnections(newConnections, oldConnections))
        throw new Error(CONNECTORS_EQUAL);
    const handleAddedOrChangedConnection = () => {
        const stringifiedOlcConnections = oldConnections.map(stringifyConnectionData);
        return newConnections.filter((connection) => {
            const stringifiedConnection = stringifyConnectionData(connection);
            return !stringifiedOlcConnections.includes(stringifiedConnection);
        });
    };
    const handleRemovedConnection = () => {
        const [smallest, largest] = [newConnections, oldConnections].sort((a, b) => a.length - b.length);
        const stringifiedSmallest = smallest.map(stringifyConnectionData);
        return largest.filter((connection) => !stringifiedSmallest.includes(stringifyConnectionData(connection)));
    };
    const unique = oldConnections.length > newConnections.length
        ? handleRemovedConnection()
        : handleAddedOrChangedConnection();
    if (!unique)
        throw new Error(UNIQUE_CONNECTION_COULD_NOT_BE_FOUND);
    const whatHappened = () => {
        if (oldConnections.length > newConnections.length)
            return DELETE;
        if (oldConnections.length < newConnections.length)
            return ADD;
        return UPDATE;
    };
    return { connections: unique, action: whatHappened() };
};
/**
 * Find an entities previous spot based on connections
 *
 * @param newConnection
 * @param oldConnections
 */
export const findChildPreviousSpot = (newConnection, oldConnections) => {
    const { hostConnectorEntity, guestConnector, guestConnectorEntity } = newConnection;
    return oldConnections.find((connection) => {
        const { hostConnectorEntity: hostOldEntity, guestConnectorEntity: guestOldEntity, guestConnector: guestOldConnector, } = connection;
        const matchingHost = hostOldEntity.id === hostConnectorEntity.id;
        const matchingGuest = guestOldEntity.id === guestConnectorEntity.id;
        const matchingConnector = guestOldConnector.key === guestConnector.key;
        return matchingHost && matchingGuest && matchingConnector;
    });
};
/**
 * Determines if a section size was changed
 * @param newConnection
 * @param oldConnection
 */
export const sectionSizeWasChanged = (newConnection, oldConnection) => MOUNTING_RAIL_RIGHT_SIDE_UPRIGHT_CONNECTORS.includes(newConnection.hostConnector.key) &&
    MOUNTING_RAIL_RIGHT_SIDE_UPRIGHT_CONNECTORS.includes(oldConnection.hostConnector.key);
/**
 * Returns connector key for the wall an entity is connector to
 * @param entity
 */
export const getWallConnectorKey = (entity) => {
    var _a;
    const connectionKey = (_a = getGuestConnections(entity).find((connection) => connection.hostConnector.key.match(ROOM_SURFACE))) === null || _a === void 0 ? void 0 : _a.hostConnector.key;
    if (!connectionKey)
        throw new Error(NO_CONNECTION_KEY);
    return connectionKey;
};
/**
 * A helper function for fetching mounting rail connectors.
 * @constructor
 */
const MountingRailConnectorGenerator = () => {
    const getHostComponent = (mountingRail) => mountingRail.getComponent(ComponentIds.HOST_CONNECTIONS_COMPONENT_ID);
    const getGuestComponent = (mountingRail) => mountingRail.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
    const getConnector = (connectorKey, relation, mountingRail) => {
        const hostComponent = relation === HOST
            ? getHostComponent(mountingRail)
            : getGuestComponent(mountingRail);
        if (!hostComponent)
            return null;
        return (hostComponent.connections.find((connection) => connection[`${relation}Connector`].key === connectorKey) || null);
    };
    const connectorsKeyObject = MOUNTING_RAIL_RIGHT_SIDE_UPRIGHT_CONNECTORS.reduce((acc, connectorKey) => (Object.assign(Object.assign({}, acc), { [connectorKey]: (mountingRail) => getConnector(connectorKey, HOST, mountingRail) })), {});
    return Object.assign(Object.assign({}, connectorsKeyObject), { [UPRIGHT_TO_MOUNTING_RAIL_LEFT]: (mountingRail) => getConnector(UPRIGHT_TO_MOUNTING_RAIL_LEFT, HOST, mountingRail), [MOUNTING_RAIL_TO_UPRIGHT_LEFT]: (mountingRail) => getConnector(MOUNTING_RAIL_TO_UPRIGHT_LEFT, GUEST, mountingRail) });
};
export const MountingRailConnectorService = MountingRailConnectorGenerator();
